import React, { useMemo, useState } from 'react';
import { X, Bell, Search, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LEAD_SOURCES } from '@/config/leadSources';
import { LEAD_SECTORS } from '@/config/leadSectors';

/**
 * Shared Add/Edit Lead modal. The two flows are 95% the same form — this
 * component renders both, switching small bits via `mode`:
 *  - mode='add'  → shows installer selector for staff, title "Add New Lead",
 *                  submit button "Add Lead".
 *  - mode='edit' → no installer selector, title "Edit Lead", submit button
 *                  "Update Lead".
 *
 * Form state lives in the parent (`formData` + `setFormData`); this component
 * is presentation-only.
 */
export default function LeadFormModal({
  open,
  mode,                 // 'add' | 'edit'
  formData,
  setFormData,
  installers,
  isStaff,
  crmStatuses,
  onSubmit,
  onClose,
}) {
  if (!open) return null;
  const isAdd = mode === 'add';

  const updateField = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50 overscroll-contain">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full flex flex-col max-h-[100dvh] sm:max-h-[92dvh] shadow-xl">
        {/* Fixed header */}
        <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-[#27292E]">{isAdd ? 'Add New Lead' : 'Edit Lead'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg -mr-2"
            aria-label="Close"
            data-testid="lead-form-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrolling body — this is the ONLY element that scrolls */}
        <form
          onSubmit={onSubmit}
          className="flex-1 min-h-0 flex flex-col"
          data-testid={`lead-form-${mode}`}
        >
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-4">
            {/* Installer Selection — only for staff in Add mode.
                Searchable picker: matches on person name AND company name. */}
            {isAdd && isStaff && (
              <div className="p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                <Label className="text-sm font-medium text-purple-800">Assign to Installer *</Label>
                <InstallerPicker
                  installers={installers}
                  value={formData.installer_id}
                  onChange={(id) => setFormData({ ...formData, installer_id: id })}
                />
                <p className="text-xs text-purple-600 mt-1">This lead will appear in the selected installer&apos;s account</p>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Sector</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1" data-testid="lead-form-sector">
                {LEAD_SECTORS.map((s) => {
                  const on = (formData.service_type || 'general') === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, service_type: s.id })}
                      data-testid={`lead-form-sector-${s.id}`}
                      className={`text-xs font-semibold px-3 py-2 rounded-lg border-2 transition-colors ${on ? 'text-white shadow' : 'bg-white text-gray-600 hover:border-gray-300'}`}
                      style={on ? { background: s.color, borderColor: s.color } : { borderColor: '#e5e7eb' }}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Pick General / Other for work that isn&apos;t resin-related.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Customer Name *</Label>
                <Input required value={formData.customer_name} onChange={updateField('customer_name')} placeholder="Enter customer name" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Phone Number</Label>
                <Input type="tel" inputMode="tel" value={formData.customer_phone} onChange={updateField('customer_phone')} placeholder="07123 456789" className="mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Email</Label>
                <Input type="email" inputMode="email" autoComplete="email" value={formData.customer_email} onChange={updateField('customer_email')} placeholder="customer@email.com" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Address</Label>
                <Input value={formData.customer_address} onChange={updateField('customer_address')} placeholder="Full address" className="mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">When do they want the project?</Label>
                <Input value={formData.project_timeline} onChange={updateField('project_timeline')} placeholder="e.g. Next month, ASAP, Spring 2025" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Rough Project Size</Label>
                <Input value={formData.project_size} onChange={updateField('project_size')} placeholder="e.g. 50 m², Medium driveway" className="mt-1" />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Project Description</Label>
              <textarea
                value={formData.project_description}
                onChange={updateField('project_description')}
                placeholder="Brief description of what the customer wants..."
                className="mt-1 w-full border-2 rounded-lg p-3 min-h-[80px] text-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <select value={formData.status} onChange={updateField('status')} className="mt-1 w-full border-2 rounded-lg p-2.5 text-base bg-white">
                  {Object.entries(crmStatuses).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium">How did they hear about us?</Label>
                <select
                  value={formData.source || ''}
                  onChange={updateField('source')}
                  className="mt-1 w-full border-2 rounded-lg p-2.5 bg-white text-base"
                  data-testid="lead-form-source"
                >
                  <option value="">— not yet asked —</option>
                  {LEAD_SOURCES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Appointment Date</Label>
                <Input type="date" value={formData.appointment_date} onChange={updateField('appointment_date')} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Appointment Time</Label>
                <Input type="time" value={formData.appointment_time} onChange={updateField('appointment_time')} className="mt-1" />
              </div>
            </div>

            {/* Reminder Section */}
            <div className="p-4 bg-amber-50 rounded-xl border-2 border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-5 h-5 text-amber-600" />
                <Label className="text-sm font-medium text-amber-800">Set Reminder</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-amber-700">Reminder Date</Label>
                  <Input type="date" value={formData.reminder_date} onChange={updateField('reminder_date')} className="mt-1 border-amber-200" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-amber-700">Reminder Note</Label>
                  <Input value={formData.reminder_note} onChange={updateField('reminder_note')} placeholder="e.g. Call back tomorrow" className="mt-1 border-amber-200" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Notes</Label>
              <textarea
                value={formData.notes}
                onChange={updateField('notes')}
                placeholder="Any additional notes..."
                className="mt-1 w-full border-2 rounded-lg p-3 min-h-[60px] text-sm"
              />
            </div>
          </div>

          {/* Sticky footer — Submit is ALWAYS visible on mobile, no scroll-hunt */}
          <div
            className="shrink-0 border-t border-gray-100 bg-white px-4 sm:px-6 py-3 flex gap-3 rounded-b-2xl"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}
          >
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-11" data-testid="lead-form-cancel">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 h-11 bg-[#006400] hover:bg-[#004d00] text-white" data-testid="lead-form-submit">
              {isAdd ? 'Add Lead' : 'Update Lead'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


/**
 * Searchable installer picker. Matches (case-insensitive) on either the
 * installer's own name or their company name so staff can find "John",
 * "Smith Landscaping", or "smith" and see the same person.
 *
 * A hidden required input mirrors the selected id so the surrounding
 * <form>'s native validation still catches an empty selection on submit.
 */
function InstallerPicker({ installers, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => installers.find((i) => i.user_id === value) || null,
    [installers, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return installers.slice(0, 50); // don't dump 500 at once
    return installers.filter((i) => {
      const name = (i.name || '').toLowerCase();
      const company = (i.company_name || '').toLowerCase();
      return name.includes(q) || company.includes(q);
    }).slice(0, 50);
  }, [installers, query]);

  const pick = (installer) => {
    onChange(installer.user_id);
    setOpen(false);
    setQuery('');
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
  };

  return (
    <div className="mt-1 relative">
      {/* Required-satisfying hidden input so the form's native "please fill in
          this field" trigger still fires when nothing is picked. */}
      <input
        type="text"
        tabIndex={-1}
        aria-hidden
        required
        value={value || ''}
        onChange={() => {}}
        className="absolute opacity-0 pointer-events-none h-0 w-0"
      />

      {selected ? (
        <div className="w-full border-2 border-purple-200 rounded-lg bg-white p-2.5 flex items-center gap-2" data-testid="installer-selected">
          <div className="w-8 h-8 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
            {(selected.name || '?').split(/\s+/).filter(Boolean).map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{selected.name || 'Unnamed'}</div>
            <div className="text-xs text-slate-500 truncate">
              {selected.company_name || 'No company'} · {selected.email}
            </div>
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-xs text-purple-700 hover:text-purple-900 font-semibold px-2 py-1 rounded hover:bg-purple-100 shrink-0"
            data-testid="installer-clear"
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder="Search by installer name or company…"
              className="w-full pl-9 pr-9 py-2.5 border-2 border-purple-200 rounded-lg bg-white text-base focus:outline-none focus:border-purple-500"
              data-testid="installer-search"
              autoComplete="off"
            />
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 pointer-events-none" />
          </div>

          {open && (
            <div
              className="absolute z-30 mt-1 left-0 right-0 bg-white border-2 border-purple-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
              data-testid="installer-dropdown"
              onMouseDown={(e) => e.preventDefault()}  // keep input focus while clicking rows
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-slate-500 text-center italic">
                  No installers match "{query}"
                </div>
              ) : (
                filtered.map((i) => (
                  <button
                    key={i.user_id}
                    type="button"
                    onClick={() => pick(i)}
                    className="w-full text-left px-3 py-2.5 hover:bg-purple-50 flex items-center gap-2 border-b border-slate-100 last:border-b-0"
                    data-testid={`installer-option-${i.user_id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-purple-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                      {(i.name || '?').split(/\s+/).filter(Boolean).map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{i.name || 'Unnamed'}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {i.company_name || 'No company'} · {i.email}
                      </div>
                    </div>
                    {value === i.user_id && <Check className="w-4 h-4 text-purple-600 shrink-0" />}
                  </button>
                ))
              )}
              {installers.length > filtered.length && !query && (
                <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t italic">
                  Showing first {filtered.length} of {installers.length} — start typing to narrow down
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

